import { useLang } from "@/context/LanguageContext";
import { Reveal } from "@/components/site/Reveal";
import { formatMonthDay, pick, useContent } from "@/lib/content";

/**
 * The legal season and daily-bag table. Plain reference material, and exactly
 * what a foreign hunter needs before booking — so it earns a place on the page
 * rather than a PDF nobody opens.
 */
export const Seasons = () => {
  const { t, lang } = useLang();
  const { data } = useContent();
  const quotas = data?.quotas ?? [];

  if (quotas.length === 0) return null;

  return (
    <section id="sezoane" data-testid="seasons-section" className="bg-bone">
      <div className="mx-auto max-w-[1200px] px-5 py-24 md:px-10 md:py-32">
        <Reveal>
          <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-rust">
            {t.seasons.overline}
          </span>
        </Reveal>
        <Reveal delay={0.08}>
          <h2 className="mt-5 font-display text-5xl font-medium uppercase leading-[0.9] tracking-tight text-ink md:text-7xl">
            {t.seasons.title}
          </h2>
        </Reveal>
        <Reveal delay={0.16}>
          <p className="mt-6 max-w-2xl font-body text-lg leading-relaxed text-stone">
            {t.seasons.body}
          </p>
        </Reveal>

        <Reveal delay={0.2}>
          {/* Wide table scrolls inside its own box; the page never does. */}
          <div className="mt-12 overflow-x-auto border border-hairline">
            <table className="w-full min-w-[560px] border-collapse bg-bone">
              <thead>
                <tr className="border-b border-hairline">
                  {[t.seasons.species, t.seasons.period, t.seasons.quota].map((h, i) => (
                    <th
                      key={h}
                      className={`px-5 py-4 font-mono text-[10px] uppercase tracking-[0.2em] text-olive ${
                        i === 2 ? "text-right" : "text-left"
                      }`}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {quotas.map((q) => (
                  <tr key={q.id} className="border-b border-hairline/50 last:border-0">
                    <td className="px-5 py-3 font-body text-sm font-medium text-ink">
                      {pick(q.names, lang)}
                    </td>
                    <td className="whitespace-nowrap px-5 py-3 font-body text-sm text-stone">
                      {formatMonthDay(q.season_from, lang)} – {formatMonthDay(q.season_to, lang)}
                    </td>
                    <td className="whitespace-nowrap px-5 py-3 text-right font-body text-sm text-ink">
                      <span className="font-display text-xl">{q.daily_quota}</span>
                      <span className="ml-1.5 text-xs text-stone">{t.seasons.pieces}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
